/**
 * UI-3.5: Action Dispatch Hook
 *
 * Unified action execution with consistent feedback.
 * Every action produces an ActionResult with the same shape,
 * regardless of whether it hit the backend or was UI-only.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { API_BASE } from '@/config/api';

// ─── Unified Result Type ────────────────────────────────────────────────────

export interface ActionResult {
  readonly status: 'success' | 'error';
  readonly message: string;
  readonly followUp?: 'refresh' | null;
}

export interface ActionFeedback {
  readonly message: string;
  readonly variant: 'success' | 'error';
}

interface ActionDispatchOptions {
  entityType: 'room' | 'guest' | 'request';
  entityId: string;
  onRefresh?: () => void;
}

// ─── Consistent Action Messages ─────────────────────────────────────────────

export const ACTION_SUCCESS_MESSAGES: Record<string, string> = {
  'escalate-maintenance': 'Escalation sent to maintenance',
  'assign-housekeeping': 'Housekeeping task assigned successfully',
  'contact-guest': 'Guest contact initiated successfully',
  'review-requests': 'Navigating to requests',
  'inspect-room': 'Navigating to room inspection',
  assign: 'Request assigned successfully',
  escalate: 'Escalation sent to supervisor',
  resolve: 'Request resolved successfully',
};

// ─── Backend Mutation ───────────────────────────────────────────────────────

export async function patchRequestStatus(
  requestId: string,
  status: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_BASE}/api/requests/${encodeURIComponent(requestId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      return { ok: false, error: (body as Record<string, string>).error ?? response.statusText };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'Network error — please try again' };
  }
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useActionDispatch({ entityType, entityId, onRefresh }: ActionDispatchOptions) {
  const [feedback, setFeedback] = useState<ActionFeedback | null>(null);
  const [executing, setExecuting] = useState(false);
  const executingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const showFeedback = useCallback((fb: ActionFeedback) => {
    setFeedback(fb);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setFeedback(null), 4000);
  }, []);

  const dismissFeedback = useCallback(() => {
    clearTimeout(timerRef.current);
    setFeedback(null);
  }, []);

  const dispatch = useCallback(
    async (actionType: string): Promise<ActionResult> => {
      // Guard: prevent duplicate execution (ref avoids stale closure)
      if (executingRef.current) {
        return { status: 'error', message: 'Action already in progress' };
      }

      executingRef.current = true;
      setExecuting(true);
      let result: ActionResult;

      try {
        // Backend mutations for request entity
        if (entityType === 'request' && (actionType === 'assign' || actionType === 'resolve')) {
          const targetStatus = actionType === 'assign' ? 'IN_PROGRESS' : 'COMPLETED';
          const res = await patchRequestStatus(entityId, targetStatus);
          result = res.ok
            ? { status: 'success', message: ACTION_SUCCESS_MESSAGES[actionType]!, followUp: 'refresh' }
            : { status: 'error', message: `Action failed — ${res.error}` };
        } else {
          // All other actions: operational acknowledgment
          result = {
            status: 'success',
            message: ACTION_SUCCESS_MESSAGES[actionType] ?? `Action acknowledged successfully`,
            followUp: null,
          };
        }
      } catch {
        result = { status: 'error', message: 'Action failed — please try again' };
      } finally {
        executingRef.current = false;
        setExecuting(false);
      }

      // Show toast feedback
      showFeedback({
        message: result.message,
        variant: result.status,
      });

      // Trigger refresh after successful backend mutations
      if (result.status === 'success' && result.followUp === 'refresh' && onRefresh) {
        onRefresh();
      }

      return result;
    },
    [entityType, entityId, onRefresh, showFeedback],
  );

  return { dispatch, feedback, dismissFeedback, executing };
}
